import { handleActions } from 'redux-actions';
import produce from 'immer';
import actions from '../point/actions';
import { updateStoreWithPath } from '../../../lib/common';
import { ACCOUNT_USER_LIST_STATUS } from '../../../constants/service/app/accountUserConstants';
import { _LIST_PAGINATION_MODEL } from '../app/models';
import {
  POINT_CAUSE_LIST_SEARCH_OPTION_MODEL,
  POINT_ISSUE_HISTORY_DETAIL_FILTER_OPTION_MODEL,
  POINT_ISSUE_HISTORY_LIST_SEARCH_DATA_MODEL,
  POINT_ISSUE_HISTORY_LIST_SEARCH_OPTION_MODEL,
  POINT_ISSUED_USER_MODEL,
  POINT_LIST_SEARCH_OPTION_MODEL,
  POINT_LIST_SORT_OPTION_MODEL,
  POINT_RANKING_LIST_SEARCH_DATA_MODEL,
  POINT_RANKING_LIST_SEARCH_OPTION_MODEL,
  POINT_RANKING_LIST_SORT_OPTION_MODEL,
  POINT_REFERRAL_ISSUE_LIST_SEARCH_DATA_MODEL,
  POINT_REFERRAL_ISSUE_LIST_SEARCH_OPTION_MODEL,
  POINT_REFERRAL_ISSUE_LIST_SORT_OPTION_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  포인트 cause SELECT LIST
   */
  pointCauseSelectList: [],

  /**
   *  포인트 내역 리스트에서 사용되는 MODEL
   */
  pointList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: POINT_LIST_SEARCH_OPTION_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: POINT_LIST_SEARCH_OPTION_MODEL,
    /**
     *  정렬 옵션
     */
    sortOption: POINT_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  admin SELECT 리스트
   */
  adminSelectList: [],

  /**
   *  포인트 랭킹에서 사용되는 MODEL
   */
  pointRanking: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: POINT_RANKING_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: POINT_RANKING_LIST_SEARCH_OPTION_MODEL,
    /**
     *  정렬 옵션
     */
    sortOption: POINT_RANKING_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  레퍼럴 발급 현황에서 사용되는 MODEL
   */
  pointReferralIssueList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: POINT_REFERRAL_ISSUE_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: POINT_REFERRAL_ISSUE_LIST_SEARCH_OPTION_MODEL,
    /**
     *  정렬 옵션
     */
    sortOption: POINT_REFERRAL_ISSUE_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  포인트 사유 리스트에서 사용되는 MODEL
   */
  pointCauseList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: POINT_CAUSE_LIST_SEARCH_OPTION_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: POINT_CAUSE_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  포인트 사유 등록에서 사용되는 MODEL
   */
  pointCauseSave: {
    pointCauseInfo: {
      /**
       *  포인트 사유 타입
       */
      type: null,
      /**
       *  포인트 사유
       */
      cause: null,
    },
  },

  /**
   *  포인트 발급받을 유저 테이블
   */
  issuedUser: POINT_ISSUED_USER_MODEL,

  /**
   *  포인트 Log 모달에서 사용하는 MODEL
   */
  pointLog: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  포인트 발급 내역 리스트
   */
  pointIssueHistoryList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: POINT_ISSUE_HISTORY_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: POINT_ISSUE_HISTORY_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  포인트 발급 내역 상세
   */
  pointIssueHistoryDetail: {
    /**
     *  포인트 발급 내역 정보
     */
    pointIssueHistoryInfo: {
      /**
       *  발급 내역 id
       */
      pointIssueHistoryId: null,
      /**
       *  발급한 관리자
       */
      adminName: null,
      /**
       *  메모
       */
      memo: null,
      /**
       *  포인트 발급 데이터 생성일
       */
      createDate: null,
    },
    /**
     *  포인트 발급 내역 아이템 리스트
     */
    pointIssueHistoryItemList: {
      /**
       *  리스트
       */
      list: [],
      /**
       *  필터 옵션
       */
      filterOption: POINT_ISSUE_HISTORY_DETAIL_FILTER_OPTION_MODEL,
      /**
       *  리스트 pagination
       */
      pagination: _LIST_PAGINATION_MODEL,
    },
  },

  /**
   *  포인트 로그 모달
   */
  pointLogModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  포인트 일괄 발급 엑셀 업로드 모달
   */
  excelBulkGeneratePointModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [포인트] 포인트 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [포인트 내역 리스트] 포인트 내역 리스트 요청하기
     */
    [actions.GET_POINT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  포인트 내역 리스트
         */
        draft.pointList = {
          ...draft.pointList,
          ...action.payload.pointList,
        };
        /**
         *  admin select 리스트
         */
        draft.adminSelectList = action.payload.adminSelectList;
        /**
         *  point cause 리스트
         */
        draft.pointCauseSelectList = action.payload.pointCauseSelectList;
      });
    },

    /**
     *  [포인트 Log 모달] 포인트 Log 요청하기
     */
    [actions.GET_POINT_LOG_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  포인트 내역 리스트
         */
        draft.pointLog = {
          ...draft.pointLog,
          ...action.payload.pointLog,
        };
      });
    },

    /**
     *  [포인트 랭킹] 포인트 랭킹 요청하기
     */
    [actions.GET_POINT_RANKING_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  포인트 내역 리스트
         */
        draft.pointRanking = {
          ...draft.pointRanking,
          ...action.payload.pointRanking,
        };
      });
    },

    /**
     *  [포인트 발급] 포인트 발급받을 유저 리스트 요청하기
     */
    [actions.GET_GENERATE_POINT_USER_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  포인트 발급받을 유저 리스트
         */
        draft.issuedUser.userList = {
          ...draft.issuedUser.userList,
          ...action.payload.userList,
        };

        /**
         *  유저 상태가 활성이지 않을 경우에는 선택 못하도록 함.
         */
        draft.issuedUser.userList.list = draft.issuedUser.userList.list.map((item) => ({
          ...item,
          uncheckable: item.uncheckable || item.status !== ACCOUNT_USER_LIST_STATUS.ACTIVATE,
        }));
      });
    },

    /**
     * [포인트 발급] 포인트 발급 전 준비 데이터 받아오기
     */
    [actions.GET_PREPARATION_GENERATE_POINT_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  point cause 리스트
         */
        draft.pointCauseSelectList = action.payload.pointCauseSelectList;
      });
    },

    /**
     * [포인트 발급 모달] 포인트 발급 하기
     */
    [actions.GENERATE_POINT_SUCCESS]: (state) => {
      return produce(state, (draft) => {
        /**
         * 페이지 초기화
         */
        draft.pointList = {
          ...initialState.pointList,
        };
        draft.issuedUser = {
          ...initialState.issuedUser,
        };
      });
    },

    /**
     *  [포인트 발급 내역 리스트] 포인트 발급 내역 리스트 가져오기
     */
    [actions.GET_POINT_ISSUE_HISTORY_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.pointIssueHistoryList = {
          ...draft.pointIssueHistoryList,
          ...action.payload.pointIssueHistoryList,
        };
        /**
         *  admin select 리스트
         */
        draft.adminSelectList = action.payload.adminSelectList;
      });
    },

    /**
     *  [포인트 발급 내역 상세] 포인트 발급 내역 상세 가져오기
     */
    [actions.GET_POINT_ISSUE_HISTORY_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.pointIssueHistoryDetail = action.payload.pointIssueHistoryDetail;
        /**
         *  point cause 리스트
         */
        draft.pointCauseSelectList = action.payload.pointCauseSelectList;
      });
    },

    /**
     *  [포인트 발급 내역 상세] 포인트 로그 리스트 요청하기
     */
    [actions.GET_POINT_ISSUE_HISTORY_POINT_LOG_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.pointIssueHistoryDetail.pointIssueHistoryItemList =
          action.payload.pointIssueHistoryDetail.pointIssueHistoryItemList;
      });
    },

    /**
     *  [레퍼럴 발급 현황] 레퍼럴 발급 리스트 가져오기
     */
    [actions.GET_POINT_REFERRAL_ISSUE_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  레퍼럴 발급 리스트
         */
        draft.pointReferralIssueList = {
          ...draft.pointReferralIssueList,
          ...action.payload.pointReferralIssueList,
        };
      });
    },

    /**
     *  [포인트 사유 리스트] 포인트 사유 리스트 가져오기
     */
    [actions.GET_POINT_CAUSE_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  포인트 사유 리스트
         */
        draft.pointCauseList = {
          ...draft.pointCauseList,
          ...action.payload.pointCauseList,
        };
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },
  },
  initialState,
);
